/**
 * whatsapp-demo-form-helpers.ts — pure logic + types for WhatsAppDemoForm.tsx.
 *
 * Extracted from the component (R5-P3 LANE L5, staged bot flow build) to keep
 * WhatsAppDemoForm.tsx under the 800-line component cap (`check:code-quality`)
 * once the staged 3-step machine was added — no behavior change from the
 * previous in-component versions. Zero JSX in this file on purpose
 * ("Extract pure logic to `.ts` utils" — importing a `.tsx` loads its whole
 * graph, `.claude/knowledge/ui-components.md`).
 *
 * Related: WhatsAppDemoForm.tsx · [[knowledge/ui-components]]
 */

import { useRef } from 'react';
import { cn } from '@/lib/utils';
import { LANDING_CONTENT } from '../constants/content';
import type { BilingualContent } from '@/features/marketing/hooks/useLocale';
import type { ParsedAttribution } from '@/lib/tracking/attribution';
import { trackEvent } from '@/lib/tracking/engagement';
import { PAPER_BAND, PAPER_RULE } from '@/features/marketing/ui/paper-tokens';

export interface DemoFormSuccessData {
  waUrl: string;
}

export type FormErrorKey =
  | 'errorRateLimited'
  | 'errorNotConfigured'
  | 'errorValidationPhone'
  | 'errorGeneric';

export type DemoFormContent = typeof LANDING_CONTENT.whatsappWedge.demoForm;

/** The staged flow's 3-state machine. `cta` is the initial/default value and
 * is never re-entered (forward-only, matches the brief's linear "big CTA ->
 * name+phone -> language+send" description — no back navigation asked for). */
export type DemoStep = 'cta' | 'contact' | 'language';

/** What the one focus-management effect in the component should move focus
 * to next — decoupled from `step` itself so a submit-time error can target
 * the EXACT owning field (ADV-20), not just "the step's first control". */
export type FocusTarget = 'cta' | 'name' | 'phone' | 'lang' | 'consent' | null;

/** LOCAL bilingual micro-copy — NOT added to content.ts (this lane does not
 * edit that file; content-surgeon owns it). "Continue" is a generic
 * step-advance affordance, not marketing/legal copy needing that review.
 * Flagged in the L5 build report as a candidate to graduate into
 * content.ts's `botSection.staged` group in a follow-up content pass. */
export const CONTINUE_LABEL: BilingualContent = {
  en: 'Continue',
  ro: 'Continuă',
};

// ── Engagement instrumentation (v2 bio-link pipeline, contract 3) ─────────
// Extracted out of WhatsAppDemoForm.tsx (mechanical, behavior-identical —
// component-file-large P2, 800-line cap): a useRef flag so the returned
// handler fires 'form_start' exactly once per mount, on the FIRST field
// interaction — not on every focus/blur cycle while filling the rest of the
// form. Plain state-plus-callback, no JSX, fits this file's "zero JSX" rule.

/** Returns a stable-identity-per-render handler that fires the
 * once-per-mount `trackEvent('form_start', eventName)` telemetry event on
 * the first call, then no-ops on every subsequent call for this mount. */
export function useFormStartTracking(eventName: string): () => void {
  const firedRef = useRef(false);
  return function handleFirstFieldFocus() {
    if (firedRef.current) return;
    firedRef.current = true;
    trackEvent('form_start', eventName);
  };
}

// ── Field validation ─────────────────────────────────────────────────────
// Kept as module-level pure functions (Rule #16 extraction pattern) so they
// aren't counted as nested function declarations against the component's
// cognitive-complexity budget.

/** Single-field checks — composed by both the staged flow's step1->step2
 * gate (name+phone only, no consent yet) and the final submit check
 * (name+phone+consent), so neither duplicates the other. */
export function validateNameField(
  name: string,
  t: (content: BilingualContent) => string,
  demoForm: DemoFormContent
): string | null {
  if (!name.trim() || name.trim().length < 2) return t(demoForm.errorValidationName);
  return null;
}

export function validatePhoneField(
  phone: string,
  t: (content: BilingualContent) => string,
  demoForm: DemoFormContent
): string | null {
  if (!phone.trim() || phone.trim().length < 6) return t(demoForm.errorValidationPhone);
  return null;
}

/** Client-side field validation for the final submit (name + phone + consent). */
export function validateDemoFormFields(
  fields: { name: string; phone: string; consent: boolean },
  t: (content: BilingualContent) => string,
  demoForm: DemoFormContent
): string | null {
  return (
    validateNameField(fields.name, t, demoForm) ??
    validatePhoneField(fields.phone, t, demoForm) ??
    (!fields.consent ? t(demoForm.errorConsentRequired) : null)
  );
}

/** Staged flow (ADV-20 "validate-before-advance"): gates the step1->step2
 * "Continuă" button — name + phone only, consent isn't on the page yet at
 * that point (it lives on step 2 with the send button). */
export function validateContactStepFields(
  fields: { name: string; phone: string },
  t: (content: BilingualContent) => string,
  demoForm: DemoFormContent
): string | null {
  return (
    validateNameField(fields.name, t, demoForm) ?? validatePhoneField(fields.phone, t, demoForm)
  );
}

/** Maps a server-sent error string to the matching bilingual content key. */
export function mapDemoServerError(
  err: string | undefined,
  demoForm: DemoFormContent
): (typeof demoForm)[FormErrorKey] {
  if (!err) return demoForm.errorGeneric;
  const low = err.toLowerCase();
  if (low.includes('rate') || low.includes('too many')) return demoForm.errorRateLimited;
  if (low.includes('not configured') || low.includes('whatsapp number')) {
    return demoForm.errorNotConfigured;
  }
  if (low.includes('phone') || low.includes('invalid')) return demoForm.errorValidationPhone;
  return demoForm.errorGeneric;
}

/** Component unmounted mid-fetch — the one fetch rejection that is NOT user-facing. */
export function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

/** Shared error-state border/ring classes for the name & phone text inputs.
 * Focus tokens per R3 judge D verdict §1.6.5: focus border #F59E0B80, focus
 * ring #F59E0B4D — exact hex, not the Tailwind amber-500 opacity utility.
 * R7 paper flip (2026-07-17, off-plan-but-necessary touch by R7 lane L3 —
 * this helper is WhatsAppDemoForm.tsx-private, not claimed by any other
 * lane): the RESTING border was `border-[#FFFFFF1A]` (a white hairline,
 * ~invisible on the new paper field background) — swapped for
 * `PAPER_RULE.hairline.border`. The error/focus/amber tokens are unchanged
 * (red-500 and amber both carry enough contrast as a 1px BORDER on either a
 * dark or paper field — WCAG's non-text 3:1 floor, not the stricter 4.5:1
 * text floor that forced the sibling red-400->red-600 TEXT swap in
 * WhatsAppDemoForm.tsx itself). */
export function getDemoInputBorderClass(hasError: boolean): string {
  if (hasError) return 'border-red-500/60 focus:border-red-500 focus-visible:ring-red-500/40';
  return `${PAPER_RULE.hairline.border} focus:border-[#F59E0B80] focus-visible:ring-[#F59E0B4D]`;
}

export interface DemoFormSubmitResult {
  success: boolean;
  waUrl?: string;
  error?: string;
}

/** Posts the demo-form payload. Pure network call, no component state. */
export async function submitDemoFormRequest(
  payload: {
    name: string;
    phone: string;
    lang: 'ro' | 'en';
    consent: boolean;
    company_website: string;
    // Bio-link/campaign attribution carried from sessionStorage (VisitBeacon) —
    // null when the visitor arrived with no tracked link this session.
    attribution: ParsedAttribution | null;
  },
  signal: AbortSignal
): Promise<DemoFormSubmitResult> {
  const response = await fetch('/api/marketing/whatsapp-demo', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    signal,
    body: JSON.stringify(payload),
  });
  return (await response.json()) as DemoFormSubmitResult;
}

/**
 * Success-overlay class derivation, extracted to avoid a nested ternary in the
 * JSX className expression (sonarjs/no-nested-conditional). Hidden before
 * success, crossfades to the house-recipe card once visible (neutral border
 * + amber check — green stays reserved for the WhatsApp-native bezel).
 * R7 paper flip: the overlay replaces the (now paper) form IN-PLACE — a
 * dark `#121418` popup here would be page chrome painting dark directly on
 * paper (banned), not a framed product exhibit, so it converts to the same
 * flat paper-card recipe as the fields it replaces. Fully opaque (no `/95`
 * alpha modifier this time, unlike the pre-R7 dark card): appending an
 * opacity suffix to a registry-exported class STRING via template
 * interpolation (`` `${PAPER_BAND.base.bg}/95` ``) would produce a class
 * whose complete literal text never appears anywhere in scanned source —
 * invisible to Tailwind's static JIT extractor (the exact class-composition
 * trap `ui/dark-tokens.ts`'s own file header documents). The underlying
 * form is already `opacity-0 pointer-events-none` once `success` is set, so
 * a fully opaque overlay reads identically in practice.
 */
export function getSuccessOverlayClass(hasSucceeded: boolean, successVisible: boolean): string {
  if (!hasSucceeded) return 'opacity-0 pointer-events-none';
  return cn(
    'z-10 border',
    PAPER_RULE.hairline.border,
    PAPER_BAND.base.bg,
    successVisible ? 'opacity-100' : 'opacity-0'
  );
}

/** Minimal HTML-escaping for the noscript fallback's `dangerouslySetInnerHTML`
 * string. Inputs are trusted content.ts copy + a config-sourced phone number
 * (never user input) — escaped anyway, defensively, at zero cost. */
export function escapeHtmlText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Mirrors `buildWaUrl()`'s `https://wa.me/<number>` shape (src/app/api/
 * marketing/whatsapp-demo/route.ts:65-69) for the STATIC no-JS fallback link
 * (ADV-11). Reads `NEXT_PUBLIC_CMG_WHATSAPP_NUMBER`, NOT the server-only
 * `CMG_WHATSAPP_NUMBER` the route uses: WhatsAppDemoForm is a 'use client'
 * component — a non-public env var resolves correctly during SSR (real Node
 * process.env) but as `undefined` in the browser bundle (Next.js only
 * inlines `NEXT_PUBLIC_`-prefixed vars identically into both), which would
 * render a correct link on first paint and silently rot it to
 * "wa.me/undefined" on any later client re-render. Not a new secret: this
 * exact number already reaches every visitor's browser today via this same
 * route's JSON response (`waUrl`, the "Deschide WhatsApp" success link).
 */
export function buildStaticWaUrl(): string | null {
  const number = process.env.NEXT_PUBLIC_CMG_WHATSAPP_NUMBER;
  return number ? `https://wa.me/${number}` : null;
}

/**
 * Builds the ADV-11 no-JS fallback's `dangerouslySetInnerHTML` string — built
 * from already-locale-resolved copy + the module-level STATIC_WA_URL
 * constant only, never live component state, so the value is stable across
 * SSR and every client re-render. Extracted out of WhatsAppDemoForm.tsx
 * (mechanical, behavior-identical — component-file-large P2, 800-line cap).
 */
export function buildNoscriptHtml(
  resolvedNoscriptCopy: string,
  staticWaUrl: string | null
): string {
  const waDisplay = staticWaUrl ? staticWaUrl.replace('https://', '') : null;
  return (
    `<p class="text-sm text-[#949BA6]">${escapeHtmlText(resolvedNoscriptCopy)}</p>` +
    (staticWaUrl
      ? `<p class="mt-2"><a class="text-sm font-semibold text-[#FBBF24] underline underline-offset-2" href="${escapeHtmlText(staticWaUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtmlText(waDisplay ?? '')}</a></p>`
      : '')
  );
}

/** Computed once at module scope — a build-time constant, not per-render data. */
export const STATIC_WA_URL = buildStaticWaUrl();

/**
 * Props for WhatsAppDemoForm.tsx — lives here with the rest of the form's
 * pure types (mechanical, behavior-identical — component-file-large P2,
 * 800-line cap; same split precedent as the helpers above).
 */
export interface WhatsAppDemoFormProps {
  /**
   * When true, the form renders in compact mode for the floating bar:
   *   - field labels are hidden via CSS (.demo-dock--floating .wa-demo-field-row label)
   *     but aria-label on each input preserves screen-reader coverage
   *   - submit button uses the shorter `demoForm.submitButtonShort` CTA
   *   - reassurance line is hidden via CSS (.demo-dock--floating .wa-demo-reassurance)
   * When false (default / attached state), the form is a full comfortable vertical stack
   * with visible labels above each input and the full long CTA.
   */
  compact?: boolean;
  /**
   * When true, renders the 3-step staged state machine (design amendment §6)
   * over the SAME fields/handlers instead of the flat stack. Additive — does
   * NOT touch `compact`; default false keeps every other caller unchanged.
   */
  staged?: boolean;
}
